import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  useAuthMe,
  useCreateProject,
  useListProjects,
  useSharedProjects,
  type ProjectType,
} from "@vorloop/api-client-react";
import { NavRail } from "../components/layout/NavRail";
import { WorkspaceSwitcher } from "../components/layout/WorkspaceSwitcher";
import { PromptComposer } from "../components/dashboard/PromptComposer";
import type { Attachment } from "../components/chat/attachments";
import { RecentProjects } from "../components/dashboard/RecentProjects";
import { SharedProjects } from "../components/dashboard/SharedProjects";

const EXAMPLES_BY_TYPE: Record<ProjectType, ReadonlyArray<string>> = {
  mobile: ["Weekly meal planner", "Beginner running tracker", "Wellness journal"],
  web: ["Portfolio site", "SaaS landing page", "Internal dashboard"],
  component: ["Animated counter", "File upload", "Combobox"],
  backend: ["Todo API", "Webhook receiver", "Markdown render"],
};

export function Dashboard() {
  const [, setLocation] = useLocation();
  const me = useAuthMe();
  const handle = me.data?.user?.handle ?? "you";

  const [prompt, setPrompt] = useState("");
  const [type, setType] = useState<ProjectType>("mobile");
  const [planMode, setPlanMode] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  // Template hand-off: Templates page stashes prompt + type in
  // sessionStorage and navigates here. Consume them once on mount.
  useEffect(() => {
    try {
      const tPrompt = sessionStorage.getItem("vp.template.prompt");
      const tType = sessionStorage.getItem("vp.template.type");
      if (tPrompt) {
        setPrompt(tPrompt);
        sessionStorage.removeItem("vp.template.prompt");
      }
      if (tType && ["mobile", "web", "component", "backend"].includes(tType)) {
        setType(tType as ProjectType);
        sessionStorage.removeItem("vp.template.type");
      }
    } catch {
      // ignore
    }
  }, []);

  const create = useCreateProject({
    onSuccess: (project) => {
      // Stash so the editor's ChatDock auto-sends as the first turn.
      try {
        sessionStorage.setItem(`vp.firstPrompt.${project.id}`, prompt.trim());
        if (attachments.length > 0) {
          sessionStorage.setItem(
            `vp.firstAttachments.${project.id}`,
            JSON.stringify(attachments),
          );
        }
      } catch {
        // sessionStorage may be unavailable (private mode); we just
        // skip the autosend and the user can re-type in the chat.
      }
      setLocation(`/editor/${project.id}`);
    },
  });
  const recents = useListProjects({ limit: 6 });
  const shared = useSharedProjects();

  const submit = () => {
    if (!prompt.trim() || create.isPending) return;
    create.mutate({ prompt: prompt.trim(), type });
    void planMode;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        background: "var(--bg-canvas)",
      }}
    >
      <NavRail handle={handle} />

      <main
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "10px 24px",
          }}
        >
          <WorkspaceSwitcher handle={handle} compact />
        </header>

        <section
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "48px 24px 24px",
            gap: 32,
          }}
        >
          <h1
            style={{
              fontSize: 30,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              margin: 0,
              textAlign: "center",
            }}
          >
            Hi <span style={{ color: "var(--accent-amber)" }}>@{handle}</span>, what do you want to make?
          </h1>

          <PromptComposer
            prompt={prompt}
            onPromptChange={setPrompt}
            selectedType={type}
            onTypeChange={setType}
            planMode={planMode}
            onPlanModeChange={setPlanMode}
            onSubmit={submit}
            pending={create.isPending}
            errorMessage={create.error?.message}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
              Try an example prompt
            </span>
            {EXAMPLES_BY_TYPE[type].map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => setPrompt(label)}
                style={{
                  padding: "8px 14px",
                  borderRadius: 18,
                  background: "var(--bg-panel)",
                  border: "1px solid var(--border-default)",
                  color: "var(--text-secondary)",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <RecentProjects items={recents.data?.items ?? []} />

          {(shared.data?.items.length ?? 0) > 0 && (
            <SharedProjects items={shared.data!.items} />
          )}
        </section>
      </main>
    </div>
  );
}
